import React, { useState, useEffect } from 'react';
import { FaTimes, FaPaperPlane, FaInfoCircle, FaSpinner } from 'react-icons/fa';
import { toast } from 'react-toastify';
import OfferService, { Offer } from '../services/OfferService';
import TenantService, { BusinessProfile } from '../../settings/company/services/TenantService';
import EmailTemplateService, { EmailTemplateType } from '../../settings/email-templates/services/EmailTemplateService';

interface SendOfferEmailModalProps {
  isOpen: boolean;
  offer: Offer;
  partnerEmail: string;
  onClose: () => void;
  onSent: () => void;
}

export default function SendOfferEmailModal({
  isOpen,
  offer,
  partnerEmail,
  onClose,
  onSent,
}: SendOfferEmailModalProps): React.JSX.Element | null {
  const [recipientEmail, setRecipientEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [sendAsMe, setSendAsMe] = useState(false);
  const [sending, setSending] = useState(false);
  const [loadingTemplate, setLoadingTemplate] = useState(false);
  const [companyProfile, setCompanyProfile] = useState<BusinessProfile | null>(null);

  useEffect(() => {
    if (isOpen) {
      setRecipientEmail(partnerEmail || '');
      setSendAsMe(false);

      // Load company profile
      TenantService.getBusinessProfile()
        .then(setCompanyProfile)
        .catch((err) => console.error('Failed to load company profile:', err));

      // Determine template type based on offer state
      const templateType: EmailTemplateType = offer.order_confirmation_number
        ? 'ORDER_CONFIRMATION'
        : 'OFFER';

      // Load template preview
      setLoadingTemplate(true);
      EmailTemplateService.preview(templateType, { offer_id: offer.id })
        .then((preview) => {
          setSubject(preview.subject);
          setMessage(preview.body_text || stripHtmlTags(preview.body_html));
        })
        .catch((err) => {
          console.error('Failed to load template preview:', err);
          // Fallback to hardcoded default
          setSubject(`Angebot ${offer.offer_number}: ${offer.title}`);
          setMessage(getDefaultMessage(offer));
        })
        .finally(() => setLoadingTemplate(false));
    }
  }, [isOpen, offer, partnerEmail]);

  const stripHtmlTags = (html: string): string => {
    return html
      .replace(/<br\s*\/?>/gi, '\n')
      .replace(/<\/p>/gi, '\n\n')
      .replace(/<[^>]+>/g, '')
      .replace(/&nbsp;/g, ' ')
      .replace(/&amp;/g, '&')
      .replace(/&lt;/g, '<')
      .replace(/&gt;/g, '>')
      .replace(/\n{3,}/g, '\n\n')
      .trim();
  };

  const getDefaultMessage = (offer: Offer): string => {
    const validUntilStr = offer.valid_until
      ? new Date(offer.valid_until).toLocaleDateString('de-DE')
      : '';

    return `Sehr geehrte Damen und Herren,

anbei erhalten Sie unser Angebot ${offer.offer_number} mit dem Titel "${offer.title}".

Gesamtbetrag: ${OfferService.formatCurrency(offer.total_gross)} (inkl. MwSt.)
${validUntilStr ? `Gueltig bis: ${validUntilStr}` : ''}

Bei Fragen stehen wir Ihnen gerne zur Verfuegung.

Mit freundlichen Gruessen`;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!recipientEmail.trim()) {
      toast.error('Bitte geben Sie eine E-Mail-Adresse ein');
      return;
    }

    if (!subject.trim()) {
      toast.error('Bitte geben Sie einen Betreff ein');
      return;
    }

    setSending(true);
    try {
      const response = await OfferService.sendEmail(offer.id, {
        recipient_email: recipientEmail,
        subject,
        message,
        send_as_me: sendAsMe,
      });
      const statusMessage = response.status === 'SENT'
        ? `Angebot gesendet und Status auf SENT aktualisiert`
        : `Angebot an ${response.sent_to || recipientEmail} gesendet`;
      toast.success(statusMessage);
      onSent();
    } catch (err: any) {
      console.error('Failed to send email:', err);
      const detail = err?.response?.data?.detail || '';
      let errorMessage = 'E-Mail konnte nicht gesendet werden';

      if (detail.toLowerCase().includes('sendgrid') || err?.response?.status === 401) {
        errorMessage = 'E-Mail-Versand fehlgeschlagen (SendGrid nicht konfiguriert). Bitte kontaktieren Sie den Support.';
      } else if (detail) {
        errorMessage = detail;
      }

      toast.error(errorMessage);
    } finally {
      setSending(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'rgba(0,0,0,0.5)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 1000,
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          width: '90%',
          maxWidth: '600px',
          maxHeight: '90vh',
          overflow: 'auto',
          boxShadow: '0 10px 40px rgba(0,0,0,0.2)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '20px 24px',
            borderBottom: '1px solid #e0e3ef',
          }}
        >
          <h2 style={{ margin: 0, fontSize: '18px', fontWeight: 600, color: '#021a4a' }}>
            Angebot per E-Mail senden
          </h2>
          <button
            onClick={onClose}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              padding: '8px',
              color: '#666',
            }}
          >
            <FaTimes size={18} />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} style={{ padding: '24px' }}>
          {/* Info */}
          <div
            style={{
              padding: '12px 16px',
              background: '#f8f9fc',
              borderRadius: '8px',
              marginBottom: '16px',
              fontSize: '14px',
              color: '#666',
            }}
          >
            <strong>Angebot:</strong> {offer.offer_number} - {offer.title}
            <br />
            <strong>Betrag:</strong> {OfferService.formatCurrency(offer.total_gross)}
          </div>

          {/* Sender Info */}
          {companyProfile && (
            <div
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '8px',
                padding: '10px 14px',
                background: '#f0fdf4',
                borderRadius: '6px',
                marginBottom: '20px',
                fontSize: '13px',
                color: '#166534',
              }}
            >
              <FaInfoCircle style={{ marginTop: '2px', flexShrink: 0 }} />
              <div>
                <strong>Von:</strong> {companyProfile.default_sender_email || 'noreply@sundaeos.com'}
                {companyProfile.reply_to_email && (
                  <span style={{ marginLeft: '12px' }}>
                    <strong>Antworten an:</strong> {companyProfile.reply_to_email}
                  </span>
                )}
              </div>
            </div>
          )}

          {/* Recipient */}
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>
              Empfaenger E-Mail <span style={{ color: '#dc2626' }}>*</span>
            </label>
            <input
              type="email"
              value={recipientEmail}
              onChange={(e) => setRecipientEmail(e.target.value)}
              placeholder="kunde@beispiel.de"
              style={inputStyle}
              required
            />
          </div>

          {/* Subject */}
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>
              Betreff <span style={{ color: '#dc2626' }}>*</span>
              {loadingTemplate && <FaSpinner className="spin" style={{ marginLeft: '8px', color: '#7c3aed' }} size={12} />}
            </label>
            <input
              type="text"
              value={subject}
              onChange={(e) => setSubject(e.target.value)}
              placeholder={loadingTemplate ? 'Lade Template...' : 'Betreff der E-Mail'}
              style={inputStyle}
              disabled={loadingTemplate}
              required
            />
          </div>

          {/* Message */}
          <div style={{ marginBottom: '20px' }}>
            <label style={labelStyle}>
              Nachricht
              {loadingTemplate && <FaSpinner className="spin" style={{ marginLeft: '8px', color: '#7c3aed' }} size={12} />}
            </label>
            <textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              rows={10}
              placeholder={loadingTemplate ? 'Lade Template...' : 'Ihre Nachricht...'}
              style={{ ...inputStyle, resize: 'vertical' }}
              disabled={loadingTemplate}
            />
          </div>

          {/* Send as me checkbox */}
          <div style={{ marginBottom: '20px' }}>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '10px',
                cursor: 'pointer',
                fontSize: '14px',
                color: '#374151',
              }}
            >
              <input
                type="checkbox"
                checked={sendAsMe}
                onChange={(e) => setSendAsMe(e.target.checked)}
                style={{
                  width: '18px',
                  height: '18px',
                  cursor: 'pointer',
                  accentColor: '#7c3aed',
                }}
              />
              <span>
                Als ich senden
                <span style={{ color: '#6b7280', fontSize: '13px', marginLeft: '6px' }}>
                  (Antworten gehen an meine E-Mail)
                </span>
              </span>
            </label>
          </div>

          {/* Note */}
          <div
            style={{
              padding: '12px 16px',
              background: '#dbeafe',
              borderRadius: '8px',
              marginBottom: '24px',
              fontSize: '13px',
              color: '#1e40af',
            }}
          >
            <div>Das Angebot wird als PDF-Anhang an die E-Mail angefuegt.</div>
            <div style={{ marginTop: '6px', fontSize: '12px', opacity: 0.85 }}>
              Diese E-Mail basiert auf Ihrem Standard-Template. Aenderungen gelten nur fuer diese E-Mail.
            </div>
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
            <button
              type="button"
              onClick={onClose}
              style={{
                padding: '10px 24px',
                border: '1px solid #e0e3ef',
                borderRadius: '10px',
                background: 'white',
                color: '#666',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
              }}
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={sending}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '10px 24px',
                border: 'none',
                borderRadius: '10px',
                background: sending ? '#ccc' : '#021a4a',
                color: 'white',
                fontSize: '14px',
                fontWeight: 600,
                cursor: sending ? 'not-allowed' : 'pointer',
              }}
            >
              <FaPaperPlane size={14} />
              {sending ? 'Sending...' : 'Send'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  display: 'block',
  marginBottom: '6px',
  fontSize: '13px',
  fontWeight: 600,
  color: '#444',
};

const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '10px 12px',
  border: '1px solid #e0e3ef',
  borderRadius: '6px',
  fontSize: '14px',
  boxSizing: 'border-box',
};
